import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { toast, Bounce } from 'react-toastify';
import { createWarehouseApi, updateWarehouseApi, uniformErrorMessage } from 'api/services/uniformManagement';
import FormModalShell, { StatusToggleRow } from 'components/modelpopup/FormModalShell';

const schema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  code: Yup.string().required('Code is required'),
  address: Yup.string(),
  contact_person: Yup.string(),
  contact_number: Yup.string(),
  status: Yup.boolean(),
  is_main: Yup.boolean(),
  can_issue_uniform: Yup.boolean()
});

const Field = ({ label, required, error, children }) => (
  <div className="mb-3">
    <label
      style={{
        display: 'block',
        fontSize: 12.5,
        fontWeight: 600,
        color: '#374151',
        marginBottom: 6,
        letterSpacing: '0.01em',
      }}
    >
      {label}
      {required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
    </label>
    {children}
    {error && (
      <small style={{ color: '#dc2626', fontSize: 12, marginTop: 4, display: 'block' }}>
        {error}
      </small>
    )}
  </div>
);

const WarehouseFormModal = ({ open, onClose, selectedWarehouse, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit, formState: { errors }, setValue, reset, setError } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { name: '', code: '', address: '', contact_person: '', contact_number: '', status: true, is_main: false, can_issue_uniform: false }
  });

  useEffect(() => {
    if (open) {
      if (selectedWarehouse) {
        setValue('name', selectedWarehouse.name);
        setValue('code', selectedWarehouse.code);
        setValue('address', selectedWarehouse.address || '');
        setValue('contact_person', selectedWarehouse.contact_person || '');
        setValue('contact_number', selectedWarehouse.contact_number || '');
        setValue('status', selectedWarehouse.status);
        setValue('is_main', !!selectedWarehouse.is_main);
        setValue('can_issue_uniform', !!selectedWarehouse.can_issue_uniform);
      } else {
        reset({ name: '', code: '', address: '', contact_person: '', contact_number: '', status: true, is_main: false, can_issue_uniform: false });
      }
    }
  }, [selectedWarehouse, open]);

  const onSubmit = (data) => {
    setLoading(true);
    const apiCall = selectedWarehouse
      ? updateWarehouseApi(selectedWarehouse.id, data)
      : createWarehouseApi(data);

    apiCall.then(() => {
      toast.success(selectedWarehouse ? 'Warehouse updated!' : 'Warehouse created!', {
        position: "top-right", autoClose: 3000, transition: Bounce
      });
      setLoading(false);
      reset();
      onSuccess();
    }).catch(e => {
      setLoading(false);
      // Field-level errors (e.g. {name: ["This field is required."]}) still
      // highlight the offending input; `status`/`error` aren't form fields, so
      // skip them here and let the toast below (which reads `error` first) be
      // the one surface for those — previously they were silently dropped by
      // setError() targeting a field name that doesn't exist in this form.
      const data = e.response?.data;
      if (data && typeof data === 'object') {
        Object.entries(data)
          .filter(([k]) => k !== 'status' && k !== 'error')
          .forEach(([k, v]) => {
            setError(k, { type: 'manual', message: Array.isArray(v) ? v.join(", ") : v });
          });
      }
      toast.error(uniformErrorMessage(e, 'Failed to save warehouse'), { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  return (
    <FormModalShell
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      title={selectedWarehouse ? "Edit Warehouse" : "Add Warehouse"}
      subtitle="Stock location details and contact information."
      submitLabel={selectedWarehouse ? "Update" : "Create"}
      loading={loading}
      width={640}
    >
      <div className="row g-3">
        <div className="col-md-6">
          <Field label="Name" required error={errors.name?.message}>
            <Controller name="name" control={control}
              render={({ field }) => <input className="form-control" {...field} placeholder="Warehouse Name" />} />
          </Field>
        </div>
        <div className="col-md-6">
          <Field label="Code" required error={errors.code?.message}>
            <Controller name="code" control={control}
              render={({ field }) => <input className="form-control" {...field} placeholder="e.g. WH-001" />} />
          </Field>
        </div>
        <div className="col-12">
          <Field label="Address" error={errors.address?.message}>
            <Controller name="address" control={control}
              render={({ field }) => <input className="form-control" {...field} placeholder="Street, City" />} />
          </Field>
        </div>
        <div className="col-md-6">
          <Field label="Contact Person" error={errors.contact_person?.message}>
            <Controller name="contact_person" control={control}
              render={({ field }) => <input className="form-control" {...field} placeholder="Full name" />} />
          </Field>
        </div>
        <div className="col-md-6">
          <Field label="Contact Number" error={errors.contact_number?.message}>
            <Controller name="contact_number" control={control}
              render={({ field }) => <input className="form-control" {...field} placeholder="+971 ..." />} />
          </Field>
        </div>
        <div className="col-12">
          <Controller name="is_main" control={control}
            render={({ field: { value, onChange } }) => (
              <StatusToggleRow
                value={value}
                onChange={onChange}
                label="Main warehouse"
                helper="Stock is received here, and transfers originate here."
                onLabel="Yes"
                offLabel="No"
              />
            )} />
        </div>
        <div className="col-12">
          <Controller name="can_issue_uniform" control={control}
            render={({ field: { value, onChange } }) => (
              <StatusToggleRow
                value={value}
                onChange={onChange}
                label="Can issue uniforms"
                helper="Staff can be issued uniforms from here."
                onLabel="Yes"
                offLabel="No"
              />
            )} />
        </div>
        <div className="col-12">
          <Controller name="status" control={control}
            render={({ field: { value, onChange } }) => (
              <StatusToggleRow
                value={value}
                onChange={onChange}
                helper="Inactive warehouses are hidden from new stock operations."
              />
            )} />
        </div>
      </div>
    </FormModalShell>
  );
};

export default WarehouseFormModal;
